import { get, patch, post } from '../../lib/api/client';
import { fromP1, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';
import type { SettingSource } from '../../components';

/**
 * Money levers, the audit log, cities, registrations and money in (BE
 * `admin-money-levers.go`; B2–B6). Reads are money.read; writes are
 * money.write and always carry a reason for money/tax levers.
 */

export type LeverKind = 'decimal' | 'float' | 'int' | 'cents' | 'bool' | 'string' | 'enum';
export type LeverGroup = 'money' | 'blue_star' | 'norms';

export interface EffectiveLever {
  key: string;
  group: LeverGroup;
  kind: LeverKind;
  requiresReason: boolean;
  min?: number;
  max?: number;
  enum?: string[];
  /** What applies now. Decimals are fractions (0.045 = 4.5%); cents are cents. */
  value: number | boolean | string;
  source: SettingSource;
  /** This locale's (or city's) own value; null = it inherits. */
  override: number | boolean | string | null;
}

export type LeverValue = number | boolean | string | null;

export interface LocaleMoneyState {
  /** maxPriceCents above 0. */
  paymentsEnabled: boolean;
  payoutsEnabled: boolean;
  /** live = a dish locale; inactive otherwise. Not editable. */
  status: 'live' | 'inactive';
}

export interface LocaleLevers {
  localeId: string;
  localeName: string;
  state: LocaleMoneyState;
  levers: EffectiveLever[];
  changed?: number;
}

export function getLocaleLevers(localeId: string, signal?: AbortSignal): Promise<LocaleLevers> {
  return get<LocaleLevers>(`/admin/locales/${localeId}/settings`, undefined, signal);
}

export function patchLocaleLevers(
  localeId: string,
  changes: Record<string, LeverValue>,
  reason: string,
): Promise<LocaleLevers> {
  return patch<LocaleLevers>(`/admin/locales/${localeId}/settings`, { changes, reason });
}

// --- cities -------------------------------------------------------------------------

export interface City {
  id: string;
  name: string;
  stateProvince: string;
  isHomeRule: boolean;
  status: 'unconfigured' | 'configured';
  firstSeenAt: string;
}

export interface Registration {
  id: string;
  entity: string;
  jurisdictionType: 'state' | 'county' | 'special_district' | 'city';
  jurisdictionRef: string;
  type: string;
  licenseNumber: string;
  effectiveDate: string;
  loadedIntoStripe: boolean;
  loadedIntoStripeAt?: string;
  loadedIntoStripeById?: string;
  filingFrequency: string;
  nextDueDate?: string;
  whoFiles: string;
  status: 'active' | 'pending' | 'closed';
  createdAt: string;
}

export interface CityRow {
  city: City;
  /** The city's own override row; null when it has none. */
  settings: Record<string, unknown> | null;
  activeRegistration: Registration | null;
}

export async function listCities(
  params: PageRequest & { stateProvince?: string; homeRule?: boolean; status?: string; searchTerm?: string },
  signal?: AbortSignal,
): Promise<Page<CityRow>> {
  return fromP1(await get<P1Response<CityRow>>('/admin/cities', { ...params }, signal));
}

export interface CityLevers {
  city: City;
  localeId: string;
  levers: EffectiveLever[];
}

export function getCityLevers(cityId: string, localeId?: string, signal?: AbortSignal): Promise<CityLevers> {
  return get<CityLevers>(`/admin/cities/${cityId}/settings`, { localeId }, signal);
}

export interface RegisterCityInput {
  licenseNumber: string;
  /** YYYY-MM-DD */
  effectiveDate: string;
  filingFrequency: 'monthly' | 'quarterly' | 'annual' | '';
  nextDueDate?: string;
  whoFiles: 'stripe' | 'kurtz_fargo' | 'internal';
  reason: string;
}

export function registerCity(cityId: string, input: RegisterCityInput): Promise<{ registration: Registration }> {
  return post<{ registration: Registration }>(`/admin/cities/${cityId}/register`, input);
}

// --- registrations ---------------------------------------------------------------------

export interface RegistrationRow {
  registration: Registration;
  /** "Avon, CO" for a city registration; otherwise the jurisdiction ref. */
  jurisdictionName: string;
}

export async function listRegistrations(
  params: PageRequest & { jurisdictionType?: string; jurisdictionRef?: string; status?: string },
  signal?: AbortSignal,
): Promise<Page<RegistrationRow>> {
  return fromP1(await get<P1Response<RegistrationRow>>('/admin/registrations', { ...params }, signal));
}

export function markLoadedInStripe(id: string, reason: string): Promise<{ registration: Registration }> {
  return post<{ registration: Registration }>(`/admin/registrations/${id}/mark-loaded-in-stripe`, { reason });
}

// --- audit log ----------------------------------------------------------------------------

export interface AuditEntry {
  id: string;
  entityType: string;
  entityId: string;
  fieldName: string;
  oldValue: LeverValue | Record<string, unknown>;
  newValue: LeverValue | Record<string, unknown>;
  reason: string;
  changedAt: string;
  changedByProfileId: string;
  changedByName?: string;
}

export async function listAuditLog(
  params: PageRequest & { entityType?: string; entityId?: string; fieldName?: string; from?: string; to?: string },
  signal?: AbortSignal,
): Promise<Page<AuditEntry>> {
  return fromP1(await get<P1Response<AuditEntry>>('/admin/lever-audit-log', { ...params }, signal));
}

// --- money in --------------------------------------------------------------------------------

export type StripePayoutStatus = 'pending' | 'in_transit' | 'paid' | 'failed' | 'canceled';

export interface StripePayout {
  id: string;
  stripePayoutId: string;
  amountCents: number;
  currency: string;
  status: StripePayoutStatus | string;
  arrivalDate?: string;
  automatic: boolean;
  failureCode?: string;
  failureMessage?: string;
  initiatedAt?: string;
  inTransitAt?: string;
  paidAt?: string;
  failedAt?: string;
  bankVerifiedAt?: string;
  bankVerifiedById?: string;
  reconciliationStatus: string;
  reconciledAt?: string;
  chargesStatus: string;
  chargesCount: number;
  chargesMatchedCount: number;
  chargesAmountCents: number;
  refundsStatus: string;
  refundsCount: number;
  refundsAmountCents: number;
  adjustmentsStatus: string;
  adjustmentsCount: number;
  adjustmentsAmountCents: number;
  otherCount: number;
  otherAmountCents: number;
  chargesExceptions?: unknown[];
  refundsExceptions?: unknown[];
  adjustmentsExceptions?: unknown[];
  otherExceptions?: unknown[];
}

export async function listStripePayouts(
  params: PageRequest & {
    status?: string;
    reconciliationStatus?: string;
    bankVerified?: boolean;
    from?: string;
    to?: string;
  },
  signal?: AbortSignal,
): Promise<Page<StripePayout>> {
  return fromP1(await get<P1Response<StripePayout>>('/admin/stripe-payouts', { ...params }, signal));
}

export function getStripePayout(id: string, signal?: AbortSignal): Promise<StripePayout> {
  return get<StripePayout>(`/admin/stripe-payouts/${id}`, undefined, signal);
}

export function markBankVerified(id: string, reason: string): Promise<StripePayout> {
  return post<StripePayout>(`/admin/stripe-payouts/${id}/bank-verified`, { reason });
}
